import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider, { type Settings as SlickSettings } from 'react-slick';
import LiveAnnouncer from '@/common/LiveAnnouncer';
import TrackWrapper from '@/common/TrackWrapper';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  ButtonsInnerWrapper,
  ButtonsOuterWrapper,
  CarouselButton,
  INACTIVE_SLIDE_OPACITY,
  SlideWrapper
} from './MarketingCarousel.styles';
import Slide from './Slide';
import useSlideCenterPadding from './useSlideCenterPadding';
import useSlideContents from './useSlideContents';
import VideoLightbox from './VideoLightbox';

// TODO: swap for the real Shaq video once it's ready
const SHAQ_VIDEO_URL = 'https://www.youtube.com/embed/LYxi39dOlSg';

function useSlideContentsAndLongestHeadings(onWatchVideoClick: () => void) {
  const slideContents = useSlideContents(onWatchVideoClick);

  // Find the longest heading & subheading for spacing purposes, so all slides
  // are the same size regardless of their individual subheading length
  const { longestHeading, longestSubHeading } = useMemo(
    () =>
      slideContents.reduce(
        (acc, current) => {
          if (current.heading.length > acc.longestHeading.length) {
            acc.longestHeading = current.heading;
          }

          if (current.subHeading.length > acc.longestSubHeading.length) {
            acc.longestSubHeading = current.subHeading;
          }

          return acc;
        },
        { longestHeading: '', longestSubHeading: '' }
      ),
    [slideContents]
  );

  return { slideContents, longestHeading, longestSubHeading };
}

export default function MarketingCarousel() {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const sliderRef = useRef<Slider>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const handleWatchVideoClick = useCallback(
    () => setIsVideoModalOpen(true),
    []
  );
  const { slideContents, longestHeading, longestSubHeading } =
    useSlideContentsAndLongestHeadings(handleWatchVideoClick);
  const slideCenterPadding = useSlideCenterPadding();
  const trackEvent = useTrackEvent();

  // react-slick clones off-screen slides (infinite + centerMode) and sets
  // aria-hidden on every non-active slide, but their links/buttons stay
  // focusable. Mirror inert to aria-hidden so keyboard/AT users skip hidden
  // slides (WCAG 2.2 AA 4.1.2). Reading the live attribute also covers clones,
  // which React props cannot reach.
  const syncInertSlides = useCallback(() => {
    const slides =
      carouselRef.current?.querySelectorAll<HTMLElement>('.slick-slide');
    slides?.forEach(slide => {
      slide.inert = slide.getAttribute('aria-hidden') === 'true';
    });
  }, []);

  // Re-sync on mount and slide change, and when a responsive re-layout
  // (breakpoint or center-padding change) makes slick re-stamp aria-hidden.
  useEffect(() => {
    syncInertSlides();
  }, [activeSlideIndex, isMobile, slideCenterPadding, syncInertSlides]);

  const handleBeforeChange = useCallback((_current: number, next: number) => {
    setActiveSlideIndex(next);
  }, []);

  const handleAfterChange = useCallback(
    (index: number) => {
      syncInertSlides();
      trackEvent('Marketing Carousel', {
        action: 'swiped',
        description: 'Slide changed',
        text: slideContents[index]?.heading || '',
        index
      });
    },
    [slideContents, syncInertSlides, trackEvent]
  );

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  const sliderSettings = useMemo<SlickSettings>(
    () => ({
      afterChange: handleAfterChange,
      arrows: false,
      beforeChange: handleBeforeChange,
      centerMode: true,
      centerPadding: `${slideCenterPadding}px`,
      cssEase: isMobile ? undefined : 'cubic-bezier(0.33, 1.00, 0.68, 1.00)',
      dots: false,
      draggable: true,
      infinite: true,
      slidesToScroll: 1,
      slidesToShow: 1,
      speed: isMobile ? undefined : 800,
      swipeToSlide: true
    }),
    [handleAfterChange, handleBeforeChange, isMobile, slideCenterPadding]
  );

  return (
    <TrackWrapper
      event={{
        customAttributes: { action: 'viewed', section: 'Marketing Carousel' },
        eventName: 'Home Feed Section'
      }}
      trackOnce
      className="py-7xl sm:pt-8xl md:pb-8xl"
    >
      <div
        className="relative"
        ref={carouselRef}
        role="region"
        aria-roledescription="carousel"
        aria-label="Marketing highlights"
      >
        <Slider ref={sliderRef} {...sliderSettings}>
          {slideContents.map((slide, index) => (
            <SlideWrapper
              key={index}
              data-testid="marketing-carousel-slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slideContents.length}`}
            >
              <Slide
                {...slide}
                isActive={index === activeSlideIndex}
                opacity={
                  index === activeSlideIndex ? 1 : INACTIVE_SLIDE_OPACITY
                }
                spacerHeading={longestHeading}
                spacerSubHeading={longestSubHeading}
              />
            </SlideWrapper>
          ))}
        </Slider>

        <LiveAnnouncer
          message={`Slide ${activeSlideIndex + 1} of ${slideContents.length}`}
        />

        <ButtonsOuterWrapper>
          <ButtonsInnerWrapper>
            <CarouselButton
              direction="left"
              onClick={goToPrev}
              aria-label="Previous slide"
              data-analytics-id="Marketing Carousel"
              data-analytics-attributes={JSON.stringify({
                description: 'prev'
              })}
              data-testid="marketing-carousel-prev"
            />
            <CarouselButton
              direction="right"
              onClick={goToNext}
              aria-label="Next slide"
              data-analytics-id="Marketing Carousel"
              data-analytics-attributes={JSON.stringify({
                description: 'next'
              })}
              data-testid="marketing-carousel-next"
            />
          </ButtonsInnerWrapper>
        </ButtonsOuterWrapper>
      </div>

      <VideoLightbox
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        title="Save time with Carvana, just like Shaq did"
        videoUrl={SHAQ_VIDEO_URL}
      />
    </TrackWrapper>
  );
}
